import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { createHomeAddress } from "@/services/customerService";
import { fetchAddressByPostalCode } from "@/services/postalCodeService";
import { toast } from "sonner";
import { Loader2, User, Phone, MapPin, Home, Building } from "lucide-react";

type AddressRegistrationDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const AddressRegistrationDialog: React.FC<AddressRegistrationDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handlePostalCodeChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    setPostalCode(value);

    if (value.length === 7 && /^\d{7}$/.test(value)) {
      setIsLoadingAddress(true);
      try {
        const fetched = await fetchAddressByPostalCode(value);
        if (fetched) {
          setAddressLine1(fetched);
        } else {
          toast.error("該当する住所が見つかりませんでした");
        }
      } catch (error: any) {
        toast.error(error.message || "住所の取得に失敗しました");
      } finally {
        setIsLoadingAddress(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await createHomeAddress({
        name,
        phone_number: phoneNumber,
        postal_code: postalCode,
        address_line1: addressLine1,
        address_line2: addressLine2,
      });
      toast.success("住所を登録しました");
      onSuccess();
      onClose();
      // Reset form
      setName("");
      setPhoneNumber("");
      setPostalCode("");
      setAddressLine1("");
      setAddressLine2("");
    } catch (err: any) {
      toast.error(err.message || "住所の登録に失敗しました");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>お届け先を登録</DialogTitle>
          <DialogDescription>
            商品の配送先住所を入力してください
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="addr-name" className="flex items-center gap-2 text-sm mb-1">
              <User className="h-4 w-4" />
              氏名
            </Label>
            <Input
              id="addr-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="山田 太郎"
              required
            />
          </div>

          <div>
            <Label htmlFor="addr-phone" className="flex items-center gap-2 text-sm mb-1">
              <Phone className="h-4 w-4" />
              電話番号（ハイフンなし）
            </Label>
            <Input
              id="addr-phone"
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ""))}
              placeholder="09012345678"
              maxLength={11}
              required
            />
          </div>

          <div>
            <Label htmlFor="addr-postal" className="flex items-center gap-2 text-sm mb-1">
              <MapPin className="h-4 w-4" />
              郵便番号（ハイフンなし）
            </Label>
            <div className="relative">
              <Input
                id="addr-postal"
                type="text"
                value={postalCode}
                onChange={handlePostalCodeChange}
                placeholder="1234567"
                maxLength={7}
                required
              />
              {isLoadingAddress && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                </div>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="addr-line1" className="flex items-center gap-2 text-sm mb-1">
              <Home className="h-4 w-4" />
              住所1
            </Label>
            <Input
              id="addr-line1"
              type="text"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              placeholder="東京都新宿区西新宿1-1-1"
              required
            />
          </div>

          <div>
            <Label htmlFor="addr-line2" className="flex items-center gap-2 text-sm mb-1">
              <Building className="h-4 w-4" />
              住所2（任意）
            </Label>
            <Input
              id="addr-line2"
              type="text"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
              placeholder="マンション名や部屋番号"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              キャンセル
            </Button>
            <Button type="submit" disabled={submitting} className="flex-1">
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  登録中...
                </>
              ) : (
                "登録する"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddressRegistrationDialog;
